import React from 'react';
import { useSettings } from '../context/SettingsContext';

/**
 * Revenue Card Component
 * Displays a single revenue metric with optional trend
 */
export default function RevenueCard({ title, amount, subtitle, trend, trendDirection, icon }) {
  const { settings } = useSettings();
  const cs = settings.currency_symbol || '$';

  // Format amount: shorten large numbers for compact display
  const formatAmount = (val) => {
    if (typeof val !== 'number') return '0.00';
    if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
    if (val >= 10000) return (val / 1000).toFixed(1) + 'K';
    return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fullAmount = typeof amount === 'number'
    ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';

  return (
    <div
      className="rounded-xl border border-slate-200 p-4 lg:p-5 dark:border-slate-800 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 hover:shadow-lg transition-shadow duration-200"
      title={`${cs}${fullAmount}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            {title}
          </p>
          <h3 className="mt-1.5 text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums whitespace-nowrap">
            {cs}{formatAmount(amount)}
          </h3>

          {subtitle && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          )}

          {trend !== undefined && trend !== null && (
            <div className={`mt-2 flex items-center gap-1 text-xs font-medium ${
              trendDirection === 'up'
                ? 'text-green-600 dark:text-green-400'
                : trendDirection === 'down'
                ? 'text-red-600 dark:text-red-400'
                : 'text-slate-600 dark:text-slate-400'
            }`}>
              <span>
                {trendDirection === 'up' ? '↑' : trendDirection === 'down' ? '↓' : '→'}
              </span>
              <span>{Math.abs(trend)}%</span>
              <span className="text-slate-400 dark:text-slate-500">vs last month</span>
            </div>
          )}
        </div>

        {icon && (
          <div className="text-2xl opacity-60 shrink-0">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}
